/**
 * SEO utility functions
 */

// Canonicalize URL
export const getCanonicalUrl = (path: string): string => {
  const baseUrl = import.meta.env.VITE_BASE_URL || 'https://www.avnearth.com';
  return `${baseUrl}${path}`;
};

// Generate structured data for organization
export const getOrganizationSchema = (): string => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'AVN - Agricultural Value-chain Network',
    url: 'https://www.avnearth.com',
    logo: 'https://www.avnearth.com/avn-logo.png',
    sameAs: [
      'https://twitter.com/avnearth',
      'https://facebook.com/avnearth',
      'https://linkedin.com/company/avnearth'
    ],
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+44-794-950-0433',
      email: 'info@avnearth.com',
      contactType: 'Customer Support'
    },
    address: {
      '@type': 'PostalAddress',
      streetAddress: '128 City Road',
      addressLocality: 'London',
      postalCode: 'EC1V 2NX',
      addressCountry: 'UK'
    },
    description: 'Restoring human dignity to food systems through ethical innovation, regenerative farming, and supply chain transparency.'
  };
  
  return JSON.stringify(schema);
};

// Generate structured data for breadcrumbs
export const getBreadcrumbSchema = (items: {name: string; url: string}[]): string => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url
    }))
  };
  
  return JSON.stringify(schema);
};

// Generate structured data for article/blog post
export const getArticleSchema = (article: {
  title: string;
  description: string;
  image: string;
  datePublished: string;
  dateModified?: string;
  author?: string;
  url: string;
}): string => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': article.url
    },
    headline: article.title,
    description: article.description,
    image: article.image,
    datePublished: article.datePublished,
    dateModified: article.dateModified || article.datePublished,
    author: {
      '@type': 'Organization',
      name: article.author || 'AVN Earth'
    },
    publisher: {
      '@type': 'Organization',
      name: 'AVN Earth',
      logo: {
        '@type': 'ImageObject',
        url: 'https://www.avnearth.com/avn-logo.png'
      }
    }
  };
  
  return JSON.stringify(schema);
};

// Generate meta tags for social media
export const getSocialTags = (data: {
  title: string;
  description: string;
  image: string;
  url: string;
  type?: string;
}): Record<string, string> => {
  return {
    'og:title': data.title,
    'og:description': data.description,
    'og:image': data.image,
    'og:url': data.url,
    'og:type': data.type || 'website',
    'twitter:card': 'summary_large_image',
    'twitter:title': data.title,
    'twitter:description': data.description,
    'twitter:image': data.image
  };
};